// CSV / Excel / PDF / Print helpers shared by the cards

// ---------- CSV ----------
export function toCSV(rows = [], headers = []) {
  const hdrs =
    Array.isArray(headers) && headers.length
      ? headers
      : Object.keys((rows && rows[0]) || {});
  const esc = (s) => {
    const t = String(s ?? "");
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const out = [hdrs.map(esc).join(",")];
  for (const r of rows || []) out.push(hdrs.map((h) => esc(r[h])).join(","));
  return out.join("\n");
}

// ---------- Blob helpers ----------
export function downloadBlob(filename, mime, data) {
  const blob = data instanceof Blob ? data : new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function openHtmlInNewTab(html) {
  try {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return !!w;
  } catch {
    const dataUrl = "data:text/html;charset=utf-8," + encodeURIComponent(html);
    window.open(dataUrl, "_blank", "noopener,noreferrer");
    return true;
  }
}

// ---------- HTML table (standalone doc) ----------
export function tableHtml(headers = [], rows = []) {
  const hdrs =
    Array.isArray(headers) && headers.length
      ? headers
      : Object.keys((rows && rows[0]) || {});
  const thead = `<tr>${hdrs.map((h) => `<th>${String(h)}</th>`).join("")}</tr>`;
  const tbody = (rows || [])
    .map(
      (r) =>
        `<tr>${hdrs.map((h) => `<td>${String(r[h] ?? "")}</td>`).join("")}</tr>`
    )
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    table{border-collapse:collapse;width:100%;font:12px system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif}
    th,td{border:1px solid #ccc;padding:4px 6px;text-align:right}
    th:first-child,td:first-child{text-align:left}
  </style></head><body><table><thead>${thead}</thead><tbody>${tbody}</tbody></table></body></html>`;
}

// ---------- Unified table export ----------
// Supports both call signatures:
//   exportTable(type, { headers, rows, title, filenameBase })
//   exportTable(type, headers, rows, title, filenameBase)
export async function exportTable(type, ...args) {
  // normalize args
  let headers = [];
  let rows = [];
  let title = "";
  let filenameBase = "export";

  if (args.length === 1 && args[0] && typeof args[0] === "object" && !Array.isArray(args[0])) {
    const opt = args[0];
    headers = opt.headers ?? [];
    rows = opt.rows ?? [];
    title = opt.title ?? "";
    filenameBase = opt.filenameBase ?? "export";
  } else {
    [headers, rows, title, filenameBase] = args;
  }

  const hdrs =
    Array.isArray(headers) && headers.length
      ? headers
      : Object.keys((rows && rows[0]) || {});
  const rws = Array.isArray(rows) ? rows : [];
  const safeTitle = String(title || "");
  const base = String(filenameBase || "export");

  if (type === "csv") {
    const csv = toCSV(rws, hdrs);
    downloadBlob(`${base}.csv`, "text/csv;charset=utf-8", csv);
    return;
  }

  if (type === "excel") {
    const html = tableHtml(hdrs, rws);
    downloadBlob(`${base}.xls`, "application/vnd.ms-excel", html);
    return;
  }

  if (type === "pdf") {
    const [{ jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);

    const doc = new jsPDF({ orientation: "landscape", unit: "pt" });
    doc.setFontSize(14);
    doc.text(safeTitle, 40, 40);

    const body = rws.map((r) => hdrs.map((h) => String(r[h] ?? "")));
    autoTable(doc, {
      head: [hdrs],
      body,
      startY: 60,
      margin: { left: 40, right: 40 },
      styles: { halign: "right", fontSize: 10 },
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
      columnStyles: { 0: { halign: "left" } },
    });

    doc.save(`${base}.pdf`);
    return;
  }

  // print
  const htmlDoc = `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
    <style>
      body{font:12px system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:16px}
      h1{font-size:18px;margin:0 0 10px 0}
      table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #ccc;padding:4px 6px;text-align:right}
      th:first-child,td:first-child{text-align:left}
    </style></head><body>
    <h1>${safeTitle}</h1>${tableHtml(hdrs, rws).replace(/^.*?<table/s,'<table').replace(/<\/table>.*$/s,'</table>')}
    <script>window.addEventListener('load',()=>setTimeout(()=>window.print(),100));</script></body></html>`;
  openHtmlInNewTab(htmlDoc);
}
